import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Phone, Mail, Globe, MessageCircle, Clock, User } from "lucide-react";
import { API, SITE, waLink } from "@/config/site";
import { usePageTitle, Reveal, Eyebrow, RoadDash } from "@/components/bits";
import BermudaMap from "@/components/BermudaMap";

export default function Contact() {
  usePageTitle("Contact");
  const { data: hoursData } = useQuery({
    queryKey: ["hours"],
    queryFn: async () => (await axios.get(`${API}/hours`)).data,
  });
  const hours = hoursData?.rows?.length ? hoursData.rows : SITE.hours;

  return (
    <>
      <section className="border-b border-white/10 bg-joell-coal/40" data-testid="contact-hero">
        <div className="container-x pb-14 pt-36 md:pb-16 md:pt-44">
          <Reveal>
            <Eyebrow>Get in touch</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-6xl">Contact JOELL</h1>
            <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
              Call, message or request a quote — we're easy to reach.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-16 md:py-24" data-testid="contact-details-section">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="border border-white/10 bg-joell-surface p-8 sm:p-10" data-testid="contact-card">
              <img src="/logo.png" alt="JOELL" className="h-16 w-auto" />
              <h2 className="mt-6 font-display text-xl font-bold uppercase leading-snug text-white">
                {SITE.company}
              </h2>
              <p className="mt-3 font-display text-sm font-semibold uppercase tracking-[0.15em] text-joell-yellow">
                &ldquo;{SITE.slogan}&rdquo;
              </p>
              <dl className="mt-8 space-y-5 text-sm">
                <div className="flex items-center gap-4">
                  <User size={16} className="shrink-0 text-joell-yellow" />
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Director</dt>
                    <dd className="mt-0.5 font-semibold text-white">{SITE.director}</dd>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Phone size={16} className="shrink-0 text-joell-yellow" />
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Phone</dt>
                    <dd className="mt-0.5">
                      <a href={SITE.phoneHref} className="font-semibold text-white transition-colors hover:text-joell-yellow" data-testid="contact-phone-link">{SITE.phone}</a>
                      {" · "}
                      <a href={SITE.phone2Href} className="font-semibold text-white transition-colors hover:text-joell-yellow" data-testid="contact-phone2-link">{SITE.phone2}</a>
                    </dd>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Mail size={16} className="shrink-0 text-joell-yellow" />
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</dt>
                    <dd className="mt-0.5 break-all"><a href={`mailto:${SITE.email}`} className="font-semibold text-white transition-colors hover:text-joell-yellow" data-testid="contact-email-link">{SITE.email}</a></dd>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Globe size={16} className="shrink-0 text-joell-yellow" />
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Website</dt>
                    <dd className="mt-0.5 font-semibold text-white">{SITE.website}</dd>
                  </div>
                </div>
              </dl>

              <RoadDash green className="my-8" />

              <div className="grid grid-cols-2 gap-3">
                <a href={SITE.phoneHref} className="btn-accent !px-4" data-testid="contact-call-button"><Phone size={15} /> Call</a>
                <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-outline !px-4" data-testid="contact-whatsapp-button"><MessageCircle size={15} /> WhatsApp</a>
                <a href={`mailto:${SITE.email}`} className="btn-outline !px-4" data-testid="contact-email-button"><Mail size={15} /> Email</a>
                <Link to="/request-quote" className="btn-outline !px-4" data-testid="contact-quote-button">Request Quote</Link>
              </div>
            </div>
          </Reveal>

          <div className="space-y-10">
            <Reveal delay={0.08}>
              <div className="border border-white/10 bg-joell-surface p-8 sm:p-10" data-testid="business-hours-card">
                <h2 className="flex items-center gap-3 font-display text-xl font-bold uppercase text-white">
                  <Clock size={18} className="text-joell-yellow" /> Business Hours
                </h2>
                <ul className="mt-6 space-y-4" data-testid="business-hours-list">
                  {hours.map((h) => (
                    <li key={h.days} className="flex items-center justify-between gap-4 border-b border-white/5 pb-4 last:border-b-0 last:pb-0">
                      <span className="text-sm text-slate-300">{h.days}</span>
                      <span className="text-right font-mono text-xs font-bold tracking-wider text-joell-yellow">{h.time}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-slate-500">Hours can change seasonally — call ahead if you're planning around a specific time.</p>
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="border border-white/10 bg-joell-black p-8 sm:p-10" data-testid="contact-map-card">
                <h2 className="font-display text-xl font-bold uppercase text-white">Island-wide service</h2>
                <p className="mt-2 text-sm text-slate-400">From Sandys to St. George's — JOELL works across all of Bermuda.</p>
                <div className="mt-6"><BermudaMap /></div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col items-center justify-between gap-6 border border-joell-yellow/40 bg-joell-yellow/5 p-8 sm:p-10 md:flex-row" data-testid="contact-quote-cta">
            <p className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">Ready when you are.</p>
            <Link to="/request-quote" className="btn-accent !px-8 !py-4" data-testid="contact-quote-cta-button">Request a Free Quote</Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
